import { useWishlistAdd } from '@/features/account/hooks/useWishlistAdd'
import { useWishlistDelete } from '@/features/account/hooks/useWishlistDelete'
import { useWishlistItems } from '@/features/account/hooks/useWishlistItems'
import { useAuth } from '@lounge-fe/auth'
import { Icon, IconButton, Portal } from '@lounge-fe/ui-kit'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { AddToWishlistProps } from './types'
import { LoginModal } from '@/features/auth/components/LoginModal'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

export const AddToWishlist = ({ productId, className }: AddToWishlistProps) => {
  const { t } = useTranslation()
  const gid = `gid://shopify/Product/${productId}`

  const queryClient = useQueryClient()
  const { mutateAsync: addToWishlist, isPending: isAdding } = useWishlistAdd()
  const { mutateAsync: removeFromWishlist, isPending: isDeleting } =
    useWishlistDelete()
  const {
    data: wishlistResponse,
    refetch,
    isLoading,
    queryKey,
    error,
  } = useWishlistItems()
  const { status } = useAuth()

  const isWishlisted = useMemo(
    () => wishlistResponse?.some((id) => id === gid) ?? false,
    [wishlistResponse, gid],
  )

  const handleChange = async (isWishlisted: boolean) => {
    await queryClient.cancelQueries({ queryKey })
    if (isWishlisted) {
      queryClient.setQueryData(queryKey, (old: string[] | undefined = []) => [
        ...old,
        gid,
      ])
      await addToWishlist({ id: gid })
    } else {
      queryClient.setQueryData(queryKey, (old: string[] | undefined = []) =>
        old.filter((id) => id !== gid),
      )
      await removeFromWishlist({ id: gid })
    }

    await refetch()
  }

  if (status !== 'authenticated' || error) {
    return (
      <LoginModal
        title={t('account.wishlist.title')}
        description={t('account.wishlist.unauthenticated_modal_description')}
        onSuccess={async () => {
          await handleChange(!isWishlisted)
        }}
        trigger={
          <IconButton
            type="button"
            className={twMerge('[&>svg]:fill-none', className)}
            aria-label={t('action.add_to_wishlist')}
            icon={<Icon.Heart />}
            disabled={isLoading || isAdding || isDeleting}
          />
        }
      />
    )
  }

  return (
    <IconButton
      type="button"
      className={className}
      aria-label={
        isWishlisted
          ? t('action.remove_from_wishlist')
          : t('action.add_to_wishlist')
      }
      icon={<Icon.Heart fill={isWishlisted ? 'currentColor' : 'none'} />}
      onClick={() => handleChange(!isWishlisted)}
      disabled={isLoading || isAdding || isDeleting}
    />
  )
}
